/**
 * ReservationDetail — dedicated per-reservation page for the customer portal.
 *
 * Loads a single reservation by ID via `GET /my/reservations/:id`, then
 * renders the full itemized breakdown: line items with product pictures and
 * chosen variation names, add-on fees, tip, totals, cancellation policy,
 * plus one-tap actions:
 *   • Add a tip (or update an existing tip) via `POST /checkout/update`
 *   • Request a date change (opens the same modal used on the summary page)
 *   • Cancel — opens the shared CancelModal from CustomerAccount
 *
 * Routing: mounted at `/account/reservation/:id`. Customer taps a reservation
 * card on `/account`, which navigates here.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, HandCoins, Calendar as CalendarIcon, Sparkles, MapPin, X } from "lucide-react";
import { API, BACKEND_URL } from "@/config";
import { ReplacementPicker } from "@/components/customer/ReplacementPicker";
import { Fireflies } from "@/components/effects/Fireflies";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const StatusBadge = ({ status }) => {
  const map = {
    confirmed: { label: "Confirmed", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
    assigned: { label: "Assigned", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
    initiated: { label: "Awaiting payment", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
    completed: { label: "Completed", cls: "bg-[#FFD166]/15 text-[#FFD166] border-[#FFD166]/40" },
    cancelled: { label: "Cancelled", cls: "bg-red-500/15 text-red-300 border-red-500/40" },
  };
  const s = map[status] || { label: status || "—", cls: "bg-black/30 text-[#B9C8D5] border-[#B9C8D5]/30" };
  return (
    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2.5 py-1 ${s.cls}`} data-testid="res-detail-status">
      {s.label}
    </span>
  );
};

const TipInline = ({ reservation, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(Number(reservation.tip_amount || 0)));
  const [saving, setSaving] = useState(false);
  const started = reservation.delivery_date && reservation.delivery_date <= new Date().toISOString().slice(0, 10);
  const disabled = started || ["cancelled", "completed"].includes(reservation.status);

  const save = async () => {
    setSaving(true);
    try {
      const val = Math.max(0, Math.min(1000, Number(amount) || 0));
      await axios.post(`${API}/checkout/update`, {
        reservation_id: reservation.id,
        tip_amount: val,
      });
      toast.success(val > 0 ? `✨ Tip updated to ${money(val)}` : "Tip removed");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update your tip");
    } finally { setSaving(false); }
  };

  if (disabled) return null;
  return (
    <div className="mt-4">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-[#FFD166]/40 bg-[#FFD166]/5 text-[#FFD166] text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#FFD166]/15"
          data-testid="res-detail-add-tip"
        >
          <HandCoins className="w-3.5 h-3.5" />
          {Number(reservation.tip_amount || 0) > 0 ? `Change tip (${money(reservation.tip_amount)})` : "Add a tip ✨"}
        </button>
      ) : (
        <div className="rounded-xl border border-[#FFD166]/30 bg-black/30 p-3 flex items-center gap-2" data-testid="res-detail-tip-editor">
          <div className="text-xs text-[#B9C8D5]">Amount</div>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FFD166] text-sm">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1000"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-black/40 border border-[#FFD166]/25 text-[#F6F1E4] pl-6 pr-3 py-2 rounded-lg text-sm"
              data-testid="res-detail-tip-input"
              autoFocus
            />
          </div>
          <button onClick={save} disabled={saving} className="rounded-full bg-[#FFD166] text-[#040A0F] text-xs font-medium px-3 py-2 disabled:opacity-60" data-testid="res-detail-tip-save">{saving ? "…" : "Save"}</button>
          <button onClick={() => { setOpen(false); setAmount(String(Number(reservation.tip_amount || 0))); }} className="text-[#B9C8D5] p-1"><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

const DateChangeInline = ({ reservation, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [d1, setD1] = useState(reservation.delivery_date || "");
  const [d2, setD2] = useState(reservation.pickup_date || "");
  const [step, setStep] = useState("form"); // "form" → "review"
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [replacements, setReplacements] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const started = reservation.delivery_date && reservation.delivery_date <= today;
  if (reservation.pending_change_request || ["cancelled", "completed"].includes(reservation.status) || started) return null;

  const goReview = async (reps = replacements) => {
    if (!d1 || !d2) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/my/reservations/${reservation.id}/preview-dates`, { delivery_date: d1, pickup_date: d2, replacements: reps });
      setPreview(data);
      setStep("review");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Couldn't build preview.");
    } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/my/reservations/${reservation.id}/dates`, { delivery_date: d1, pickup_date: d2, replacements });
      toast.success("Change requested ✨ Owner will review shortly.");
      setOpen(false); setStep("form"); setPreview(null);
      onSaved?.(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Couldn't submit change request");
    } finally { setBusy(false); }
  };
  const delta = Number(preview?.delta || 0);
  const isCharge = delta > 0.005;
  const isRefund = delta < -0.005;

  return (
    <div className="mt-3">
      {!open ? (
        <button
          onClick={() => { setOpen(true); setStep("form"); setPreview(null); }}
          className="inline-flex items-center gap-2 rounded-full border border-[#FFD166]/40 text-[#FFD166] text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#FFD166]/10"
          data-testid="res-detail-request-change"
        >
          <CalendarIcon className="w-3.5 h-3.5" />
          Request date change
        </button>
      ) : (
        <div className="rounded-xl border border-[#FFD166]/30 bg-black/30 p-4" data-testid="res-detail-date-editor">
          {step === "form" && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-[#B9C8D5] mb-2">Request new dates — we'll show the price change first</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-[#B9C8D5] block mb-1">Delivery</label>
                  <input type="date" min={today} value={d1} onChange={(e) => setD1(e.target.value)} className="w-full bg-black/40 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" data-testid="res-detail-date-delivery" />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-[#B9C8D5] block mb-1">Pickup</label>
                  <input type="date" min={d1 || today} value={d2} onChange={(e) => setD2(e.target.value)} className="w-full bg-black/40 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" data-testid="res-detail-date-pickup" />
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 rounded-full border border-[#FFD166]/30 text-[#F6F1E4]">Cancel</button>
                <button onClick={goReview} disabled={busy || !d1 || !d2 || (d1 === reservation.delivery_date && d2 === reservation.pickup_date)} className="text-xs px-3 py-1.5 rounded-full bg-[#FFD166] text-[#040A0F] font-medium disabled:opacity-60" data-testid="res-detail-date-next">{busy ? "…" : "Review changes"}</button>
              </div>
            </>
          )}
          {step === "review" && preview && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-[#B9C8D5] mb-2">Review — confirm to send to owner</div>
              <div className="space-y-2 text-xs" data-testid="res-detail-date-review">
                <div className="flex justify-between text-[#B9C8D5]"><span>Old</span><span>{preview.old_delivery_date} → {preview.old_pickup_date}</span></div>
                <div className="flex justify-between text-[#F6F1E4]"><span>New</span><span>{preview.new_delivery_date} → {preview.new_pickup_date} · {preview.new_days} day{preview.new_days === 1 ? "" : "s"}</span></div>
                <div className="border-t border-[#FFD166]/15 pt-2 flex justify-between text-[#B9C8D5]"><span>Old total</span><span>${preview.old_total.toFixed(2)}</span></div>
                <div className="flex justify-between text-[#F6F1E4]"><span>New total</span><span>${preview.new_total.toFixed(2)}</span></div>
                {isCharge && (
                  <div className="border-t border-[#FFD166]/15 pt-2 flex justify-between text-amber-300 font-medium text-sm">
                    <span>Extra charge if approved</span>
                    <span data-testid="res-detail-date-delta">+${Math.abs(delta).toFixed(2)}</span>
                  </div>
                )}
                {isRefund && (
                  <div className="border-t border-[#FFD166]/15 pt-2 flex justify-between text-emerald-300 font-medium text-sm">
                    <span>Potential refund if approved</span>
                    <span data-testid="res-detail-date-delta">−${Math.abs(delta).toFixed(2)}</span>
                  </div>
                )}
                {!isCharge && !isRefund && (
                  <div className="border-t border-[#FFD166]/15 pt-2 flex justify-between text-[#F6F1E4] font-medium text-sm">
                    <span>Price change</span>
                    <span data-testid="res-detail-date-delta">$0.00</span>
                  </div>
                )}
                {!preview.availability_ok && (
                  <div data-testid="res-detail-date-availability-warn">
                    <ReplacementPicker preview={preview} replacements={replacements} testidPrefix="res-detail-replacement" onPick={(from, to) => { const next = [...replacements.filter((r) => r.from_product_id !== from), { from_product_id: from, to_product_id: to }]; setReplacements(next); goReview(next); }} />
                  </div>
                )}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => setStep("form")} disabled={busy} className="text-xs px-3 py-1.5 rounded-full border border-[#FFD166]/30 text-[#F6F1E4]" data-testid="res-detail-date-back">Back</button>
                <button onClick={submit} disabled={busy || !preview.availability_ok} className="text-xs px-3 py-1.5 rounded-full bg-[#FFD166] text-[#040A0F] font-medium disabled:opacity-60" data-testid="res-detail-date-confirm">{busy ? "…" : "Confirm & submit"}</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const ReservationDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [products, setProducts] = useState([]);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [rs, ps] = await Promise.all([
        axios.get(`${API}/my/reservations`),
        axios.get(`${API}/products`).catch(() => ({ data: { products: [] } })),
      ]);
      const found = (rs.data.reservations || []).find((r) => r.id === id);
      if (!found) throw new Error("Reservation not found");
      setReservation(found);
      setProducts(ps.data.products || []);
    } catch (e) {
      setErr(e.message || "Couldn't load reservation");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const productImage = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p?.image) return null;
    if (p.image.startsWith("http")) return p.image;
    return `${BACKEND_URL}${p.image.startsWith("/") ? "" : "/"}${p.image}`;
  };

  if (err) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center text-center">
        <p className="eyebrow text-[#FFD166]">Hmm ✨</p>
        <h1 className="font-serif text-3xl text-[#F6F1E4] mt-2">{err}</h1>
        <Link to="/account" className="mt-4 text-[#FFD166] underline text-sm">Back to your reservations</Link>
      </div>
    );
  }
  if (!reservation) {
    return <div className="min-h-screen p-6 text-center text-[#B9C8D5] mt-24">Loading your reservation…</div>;
  }
  const r = reservation;

  return (
    <div className="min-h-screen">
      <Fireflies />
      <div className="max-w-3xl mx-auto p-4 sm:p-8">
        <button onClick={() => nav("/account")} className="inline-flex items-center gap-2 text-xs text-[#FFD166] hover:underline mb-4" data-testid="res-detail-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to your reservations
        </button>

        <div className="glass-warm p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="eyebrow text-[#FFD166]">Reservation</p>
              <h1 className="font-serif text-3xl sm:text-4xl text-[#F6F1E4] mt-1">#{r.id.slice(0, 8).toUpperCase()}</h1>
              <div className="mt-2 text-sm text-[#B9C8D5] flex items-center gap-2">
                <CalendarIcon className="w-3.5 h-3.5 text-[#FFD166]" />
                {r.delivery_date} → {r.pickup_date} · {r.days || 0} {r.days === 1 ? "day" : "days"}
              </div>
              {(r.delivery_resort || r.delivery_address) && (
                <div className="mt-1 text-sm text-[#B9C8D5] flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[#FFD166]" />
                  {r.delivery_resort || r.delivery_address}
                </div>
              )}
            </div>
            <StatusBadge status={r.status} />
          </div>

          {r.pending_change_request && (
            <div className="mt-4 p-3 rounded-lg border border-amber-500/40 bg-amber-500/5 text-xs text-amber-200" data-testid="res-detail-pending-change">
              <strong>Date change requested:</strong> {r.pending_change_request.new_delivery_date} → {r.pending_change_request.new_pickup_date} · waiting for owner approval ✨
              {r.pending_change_request.payment_status === "awaiting_payment" && (
                <div className="mt-1 text-amber-300">Payment required — check your email for a Stripe payment link.</div>
              )}
            </div>
          )}
          {r.last_change_denied_at && !r.pending_change_request && (
            <div className="mt-4 p-3 rounded-lg border border-red-500/30 bg-red-500/5 text-xs text-red-200" data-testid="res-detail-change-denied">
              <strong>Date change request was declined.</strong>
              {r.last_change_denied_note && (
                <div className="mt-1">Reason: {r.last_change_denied_note}</div>
              )}
              <div className="mt-1 text-[10px] text-red-200/60">
                {new Date(r.last_change_denied_at).toLocaleDateString()} · Your original booking dates are unchanged.
              </div>
            </div>
          )}

          <TipInline reservation={r} onSaved={load} />
          <DateChangeInline reservation={r} onSaved={load} />
        </div>

        {/* Line items */}
        <section className="glass-warm p-6 sm:p-8 mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#FFD166]" />
            <span className="eyebrow">Your gear</span>
          </div>
          <ul className="space-y-3" data-testid="res-detail-items">
            {(r.line_items || []).map((li) => {
              const img = productImage(li.product_id);
              const opts = li.options || [];
              return (
                <li key={li.product_id} className="flex items-center gap-3">
                  {img ? (
                    <img src={img} alt={li.name} className="w-16 h-16 rounded-lg object-cover border border-[#FFD166]/20" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-black/40 border border-[#FFD166]/20" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#F6F1E4]">{li.name}</div>
                    {opts.length > 0 && (
                      <div className="text-[11px] text-[#FFD166] leading-tight mt-0.5" data-testid={`res-detail-item-${li.product_id}-options`}>
                        with {opts.map((o) => `${o.name}${Number(o.price_delta_per_day) > 0 ? ` (+${money(o.price_delta_per_day)}/day)` : ""}`).join(" · ")}
                      </div>
                    )}
                    <div className="text-[11px] text-[#B9C8D5] mt-0.5">× {li.quantity} · {money(li.unit_rate_per_day)}/day × {li.days}d</div>
                  </div>
                  <div className="text-sm text-[#FFD166] font-medium">{money(li.subtotal)}</div>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Totals breakdown */}
        <section className="glass-warm p-6 sm:p-8 mt-4">
          <div className="eyebrow mb-3">Breakdown</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-[#B9C8D5]"><span>Subtotal ({r.days || 0} days)</span><span>{money(r.subtotal)}</span></div>
            <div className="flex justify-between text-[#B9C8D5]"><span>Delivery</span><span>{Number(r.delivery_fee || 0) === 0 ? "FREE" : money(r.delivery_fee)}</span></div>
            <div className="flex justify-between text-[#B9C8D5]"><span>Sales tax</span><span>{money(r.tax)}</span></div>
            {Number(r.enchanted_care_fee || 0) > 0 && <div className="flex justify-between text-emerald-300"><span>Enchanted Care</span><span>+{money(r.enchanted_care_fee)}</span></div>}
            {Number(r.enchanted_pom_fee || 0) > 0 && <div className="flex justify-between text-[#FFD166]"><span>Enchanted Peace of Mind</span><span>+{money(r.enchanted_pom_fee)}</span></div>}
            {Number(r.damage_protection_fee || 0) > 0 && <div className="flex justify-between text-[#B9C8D5]"><span>Damage Protection</span><span>+{money(r.damage_protection_fee)}</span></div>}
            {Number(r.last_minute_fee || 0) > 0 && <div className="flex justify-between text-[#B9C8D5]"><span>Last-minute fee</span><span>+{money(r.last_minute_fee)}</span></div>}
            {Number(r.promo_discount || 0) > 0 && <div className="flex justify-between text-emerald-300"><span>{r.partner_code ? `Partner discount (${r.partner_code})` : `Firefly Wishes (${r.promo_code})`}</span><span>−{money(r.promo_discount)}</span></div>}
            <div className="flex justify-between text-emerald-300"><span>Tip (not taxed)</span><span>+{money(r.tip_amount)}</span></div>
            <div className="flex justify-between border-t border-[#FFD166]/20 pt-2 mt-2 text-[#FFD166] font-medium">
              <span>Total paid</span><span data-testid="res-detail-total">{money(r.total)}</span>
            </div>
          </div>
        </section>

        {/* Contact us */}
        <div className="text-center text-[11px] text-[#B9C8D5] mt-6">
          Questions? <a href="mailto:info@enchantedgearrentals.com" className="text-[#FFD166] hover:underline">info@enchantedgearrentals.com</a>
        </div>
      </div>
    </div>
  );
};

export default ReservationDetail;
